import '@styles/modal.scss';
import ReactDOM from 'react-dom';
import { ImageButton } from '@components/imagebutton';
import { CloseIcon } from '@components/closeicon';
import { ModalProps } from 'interface';
import { ModalType } from '../../enums';
import Typography from '@components/typography';
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import useToggleLiveChat from '@hooks/usetogglelivechat';
import { useDeviceDetect } from '@hooks/usedevicedetect';

export const Modal: React.FC<ModalProps> = ({
  'data-cy': dataCy,
  visible,
  content,
  toggle,
  modalType,
  className,
  isClosable = true,
}) => {
  // Refs for the lightbox and two types of dialog
  const lightboxRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [visibleDelay, setVisibleDelay] = useState(visible);

  const { hideElement, showElement } = useToggleLiveChat();
  const { isMobile } = useDeviceDetect();

  useEffect(() => {
    // Delay to allow component to render, then apply CSS (so it animates)
    setTimeout(() => {
      setVisibleDelay(visible);
      if (isMobile && visible) {
        hideElement();
      } else {
        showElement();
      }
    }, 1);
  }, [visible]);

  const isInvalidQuote: boolean = modalType === ModalType.invalidQuote;

  const closeModal = (): void => {
    // Toggle off all the
    if (modalRef.current?.classList.contains('modal-content__bch-qualify')) {
      window.dataLayer?.push({
        event: 'bch_popup',
        category: 'BCH Confirmation Popup',
        action: 'click',
        label: 'Close',
      });
    }
    lightboxRef.current?.classList.remove('lightbox--visible');
    modalRef.current?.classList.remove('modal-content--visible');

    // Delay to match the CSS transition in modal.scss
    setTimeout(() => {
      toggle(null);
    }, 300);
  };

  const handleClose = (e: React.MouseEvent<HTMLElement>): void => {
    const eventTargetElement = e.target as HTMLElement;

    if (!eventTargetElement.classList.contains('lightbox')) {
      return;
    }

    if (isMobile) closeModal();
  };

  const getModal = () => {
    return (
      <div
        ref={modalRef}
        className={classNames(
          'modal-content',
          visibleDelay && 'modal-content--visible',
          isInvalidQuote && 'modal-content__invalid-quote',
          className
        )}
        role="dialog"
        aria-modal="true"
        data-cy={`${dataCy}-modal`}
        data-testid={`${dataCy}-modal`}
      >
        {isClosable && (
          <ImageButton
            variant="text"
            ButtonIcon={CloseIcon}
            onClick={closeModal}
            ariaLabel="Close Dialog"
          ></ImageButton>
        )}
        <div className="modal-content__body">
          {content && content.icon}
          <Typography
            variant="h3"
            size="xl"
            weight="bold"
            className={classNames(
              'modal-content__body-title',
              isInvalidQuote && 'modal-content__invalid-quote-title'
            )}
          >
            {content && content.title}
          </Typography>

          <Typography
            variant="caption"
            size="rg"
            className={classNames(
              'modal-content__body-message',
              isInvalidQuote && 'modal-content__invalid-quote-message'
            )}
          >
            {content && content.message}
          </Typography>
          <div className="modal-content__body-buttons">
            {content && content.buttons}
          </div>
        </div>
      </div>
    );
  };

  return visible ? (
    ReactDOM.createPortal(
      <>
        {!isClosable && <div className="lightbox-overlay"></div>}
        <div
          ref={lightboxRef}
          className={classNames(
            'lightbox',
            visibleDelay && 'lightbox--visible',
            'lightbox--modal',
            !isClosable && 'lightbox--nonclosable'
          )}
          data-cy={dataCy}
          onClick={handleClose}
        >
          {getModal()}
        </div>
      </>,
      document.body
    )
  ) : (
    <></>
  );
};

export default Modal;
